import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

/**
 * LP の組み立て部品。
 *
 * 正は docs/design/INOU_Base_Design.dc.html の `1a LP`。
 * 面の区切りは 1px のボーダーだけ、数値と識別子は等幅、色はトークン経由。
 *
 * ## 幅への当て方
 *
 * **768px 以上はデザインの正そのままで、1px も動かさない。**
 * モバイルは「同じ設計を狭い幅に置き直したもの」であって別のデザインではない。
 * 変えるのは寸法だけで、面の分け方・罫線・色・語彙は共通に保つ。
 *
 * 段は次の 2 通りだけを使う。**新しいブレークポイントは足さない。**
 *
 * | 対象 | 段 |
 * | --- | --- |
 * | 外枠 (ページの左右余白・節の上下余白) | 3 段: 素 / `sm:` (640) / `md:` (768 = 正) |
 * | 部品 (カードの内側・見出し・数値) | 2 段: 素 / `sm:` (640 = 正) |
 *
 * どちらも 768px 以上では正の値に戻るので、デスクトップの見た目は
 * `md:` の列を読むだけで確かめられる。
 */

/**
 * 公開ページのコンテンツ幅を揃える枠。
 *
 * 背景と罫線はビューポート幅いっぱいに伸ばしたいので、それを持つ要素
 * (header / section / footer) には当てず、その内側でこれを使う。
 * 上限は `--container-page` (tokens.css) の 1 箇所で決める。
 *
 * **左右の余白はここが唯一の出どころ。** 公開ページのヘッダー・本文・
 * フッターが全部これを被るので、狭い幅で本文に残る幅もここ 1 箇所で決まる
 * (56px を素のまま当てると、375px の画面では 3 割が余白に消える)。
 */
export function PageContainer({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('mx-auto w-full max-w-page px-5 sm:px-8 md:px-14', className)}>
      {children}
    </div>
  );
}

/** 節の頭に置く英字ラベル */
export function Eyebrow({ children }: { children: ReactNode }) {
  return (
    <span className="font-mono text-[13px] tracking-[0.14em] text-ink-subtle">{children}</span>
  );
}

/**
 * 節の見出し。
 *
 * 36px (text-4xl) のままだと、日本語の見出しは 375px の幅で 2〜3 行に割れる。
 * 割れること自体より、割れ方が幅ごとに変わって「記録は、こう積 / み上がります」
 * のように語の途中で切れるのが問題なので、狭い幅では字の方を落とす。
 */
export function SectionHeading({ children }: { children: ReactNode }) {
  return <h2 className="text-[26px] font-bold tracking-tight sm:text-4xl">{children}</h2>;
}

/** 節の頭 (Eyebrow + 見出し + 導入文) と本体の間隔 */
export function SectionIntro({ children }: { children: ReactNode }) {
  return <div className="flex flex-col gap-2 pb-7 sm:pb-10">{children}</div>;
}

export function Section({
  id,
  children,
  className,
}: {
  id?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section id={id} className="border-t border-line">
      <PageContainer className={cn('py-12 sm:py-16 md:py-20', className)}>{children}</PageContainer>
    </section>
  );
}

/**
 * 数値タイル。
 *
 * 狭い幅では 1 列に積むことになるが、そのまま縦に組むと 1 つで 2 行を使い、
 * 3 つ並べるだけで画面の高さを丸ごと食う。**モバイルだけ横組みにする**——
 * 値の桁を等幅の固定幅で押さえてラベルを右に置くと、積んだときに値が縦に揃い、
 * 表として読める。640px 以上は正の縦組みに戻す。
 */
export function Metric({ value, label }: { value: string; label: string }) {
  return (
    <div className="flex items-baseline gap-3 sm:flex-col sm:items-stretch sm:gap-1">
      {/*
        幅は下限だけを決める (`min-w-`)。固定幅にすると「14日」や「112%」が
        桁の途中で折れるし、値の方を縮めると数字が読めなくなる。
        下限で揃えておけば、収まる限りラベルの左端は縦に並ぶ。
      */}
      <span className="min-w-[4.25rem] shrink-0 font-mono text-[26px] whitespace-nowrap text-brand-700 sm:min-w-0 sm:shrink sm:text-[32px]">
        {value}
      </span>
      <span className="text-sm text-ink-subtle">{label}</span>
    </div>
  );
}

/** 「仕組み」の 1 ステップ */
export function Step({
  index,
  persona,
  title,
  body,
  children,
}: {
  index: string;
  /** 誰の話かの見出し (ENGINEER / MANAGER)。DOM の role とは無関係。 */
  persona: string;
  title: string;
  body: string;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-4 border border-line bg-surface-raised px-5 py-6 sm:px-9 sm:py-9">
      <span className="font-mono text-[13px] tracking-[0.14em] text-ink-subtle">
        {index} / {persona}
      </span>
      <h3 className="text-xl font-bold">{title}</h3>
      <p className="text-base leading-relaxed text-ink-muted">{body}</p>
      {children}
    </div>
  );
}

/**
 * 「集計できること」のカード。
 *
 * レポート API (`/api/reports/*`) が実際に返す列だけを並べる。ここに無いものを
 * 載せると、契約してから「その数字は出ない」と言うことになる。
 */
export function ReportCard({
  name,
  axis,
  summary,
  columns,
  note,
}: {
  name: string;
  /** 集計の軸 (何 × 何で出るか) */
  axis: string;
  summary: string;
  /** 実際に出る列 */
  columns: { label: string; detail: string }[];
  note: string;
}) {
  return (
    <div className="flex flex-col gap-6 border border-line bg-surface-raised px-5 py-6 sm:px-9 sm:py-9">
      <div className="flex flex-col gap-2">
        <h3 className="text-xl font-bold">{name}</h3>
        <span className="font-mono text-[13px] tracking-[0.08em] text-ink-subtle">{axis}</span>
        <p className="pt-1 text-base leading-relaxed text-ink-muted">{summary}</p>
      </div>

      <dl className="flex flex-col border-t border-line">
        {columns.map((column) => (
          <div
            key={column.label}
            className="grid gap-1 border-b border-line py-4 sm:grid-cols-[10.5rem_1fr] sm:gap-4"
          >
            <dt className="text-[15px] font-bold text-ink-secondary">{column.label}</dt>
            <dd className="text-[15px] leading-relaxed text-ink-muted">{column.detail}</dd>
          </div>
        ))}
      </dl>

      <span className="mt-auto text-sm leading-relaxed text-ink-subtle">{note}</span>
    </div>
  );
}

/** 料金プランのカード */
export function PriceCard({
  name,
  audience,
  price,
  unit,
  seatNote,
  summary,
  features,
  recommended,
  action,
}: {
  name: string;
  audience: string;
  price: string;
  unit: string;
  /** このプランがどのロールのシートに当たるか */
  seatNote: string;
  summary: string;
  features: string[];
  recommended?: boolean;
  action: ReactNode;
}) {
  return (
    <div
      className={cn(
        'relative flex flex-col gap-6 border bg-surface-raised px-5 py-6 sm:px-10 sm:py-10',
        recommended ? 'border-line-bold' : 'border-line',
      )}
    >
      {recommended ? (
        <span className="absolute -top-px -right-px bg-ink px-3 py-1.5 font-mono text-xs tracking-[0.08em] text-surface-raised">
          RECOMMENDED
        </span>
      ) : null}

      <div className="flex flex-col gap-1">
        <h3 className="text-xl font-bold">{name}</h3>
        <span className="text-sm text-ink-subtle">{audience}</span>
      </div>

      <div className="flex flex-col gap-2">
        <div className="flex items-baseline gap-2">
          <span className="font-mono text-[42px] leading-none text-brand-700 sm:text-[54px]">
            {price}
          </span>
          <span className="text-sm text-ink-subtle">{unit}</span>
        </div>
        <span className="font-mono text-xs tracking-[0.06em] text-ink-faint">{seatNote}</span>
      </div>

      <p className="text-base leading-relaxed text-ink-muted">{summary}</p>

      <ul className="flex flex-col gap-2.5 border-t border-line pt-6">
        {features.map((feature) => (
          <li key={feature} className="flex gap-2.5 text-[15px] text-ink-secondary">
            <span aria-hidden className="font-mono text-ink-faint">
              —
            </span>
            {feature}
          </li>
        ))}
      </ul>

      <div className="mt-auto pt-2">{action}</div>
    </div>
  );
}
